import { Component } from 'react';
import GameData from "../mobx/GameData";
import { Accordion, Container, Row, Col, Card, Button } from "react-bootstrap";
import { CONSTANTS } from '../config/Constants';
import RomAddressOffsetDialog from '../components/dialogs/RomAddressOffsetDialog';
import ApplicationState from '../mobx/ApplicationState';

import "./CustomCodePage.scss";

export default class CustomCodePage extends Component {

    constructor(props) {
        super(props);
        this.state = {
            awaitingConfirm: false,
            awaitingConfirmAction: 'Do it',
            awaitingConfirmTitle: 'Delete Game?',
            dialogVisibility: {
                romOffset: false,
                oldVersion: false
            }
        };

    }

    async componentDidMount() {
    }

    componentWillUnmount() {
    }

    doDialog(event, name, showHide) {
        event?.preventDefault();
        this.setState({dialogVisibility: {...this.state.dialogVisibility, [name]: showHide}});
    }


    async generateCodeZip(includeBaseZip) {
        const jszip = (await import('jszip')).default;
        let zip
        const game = GameData.currentGame,
            root = `nes-puzzle-maker-rom-${CONSTANTS.GithubTag}`;
        
        if (includeBaseZip) {
            try {
                const rawdata = await (await fetch(CONSTANTS.ZipDownloadUrl)).arrayBuffer();
                zip = await jszip.loadAsync(rawdata);
            } catch (e) {
                ApplicationState.addAlert('warning', 'Unable to download zip to create code package');
                console.error('Failed getting code zip', e);
                return;
            }
        } else {
            zip = new jszip();
        }

        const patchableDataTxt = await GameData.generatePatchableDataForCurrentRom();
        zip.file(`${root}/source/assembly/library/patchable_data.asm`, patchableDataTxt);
        // NOTE: Can't do sound due to how I import it. Kinda just living with that. If you really really
        // want it, add it in the defined spot.
        if (game.soundtrackData && game.soundtrackData.length > 0) {
            zip.file(`${root}/sound/music.bin`, new Uint8Array(game.soundtrackData))
        }
        zip.file(`${root}/graphics/tiles_mod.chr`, new Uint8Array(game.gameGraphicsTiles))
        zip.file(`${root}/graphics/sprites_mod.chr`, new Uint8Array(game.gameGraphicsSprites))
        zip.file(`${root}/graphics/ascii_mod.chr`, new Uint8Array(game.gameGraphicsFont))

        const result = await zip.generateAsync({type: 'base64'});
        
        const a = document.createElement('a');
        a.setAttribute('href', 'data:application/zip;base64,' + result);
        a.setAttribute('download', 'game.zip');
        a.click();

    }

    async downloadPatchableData(e) {
        e.preventDefault();
        const string = await GameData.generatePatchableDataForCurrentRom();

        const a = document.createElement('a');
        a.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(string));
        a.setAttribute('download', 'patchable_data.asm');
        a.click();
    }

    getIpsData(e) {
        e.preventDefault();
        window.getIpsData();
    }

    render() {
        return (
            <div className="custom-code-page">
                <Container className="custom-code-page">
                    <Row>
                        <Col>
                            <h1>Custom Code</h1>
                            <p className="mt-3">
                                Retro Puzzle Maker was made to be extended! The source code is meant to be easily edited, and
                                the tools are all available right on this page.
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={8}>
                            <Card>
                                <Card.Header>Editing Your Game</Card.Header>
                                <Card.Body>
                                    <Card.Text as="div" className="inner">
                                        <h3>Building Your Game</h3>
                                        <p>Building your game is an easy process, and can be done on either Windows or Linux. (Ubuntu officially supported, others may work.) Follow the steps below.</p>
                                        <ol>
                                            <li>Download and set up <a target="_blank" rel="noreferrer" href="https://create-nes-game.nes.science/#/?id=getting-started">create-nes-game</a></li>
                                            <li><Button variant="primary" onClick={() => this.generateCodeZip(true)}>Download Your Game's Source Code</Button></li>
                                            <li>Extract the zip file to a new directory</li>
                                            <li>Open a command line (<code>Command Prompt</code> or <code>Terminal</code>)</li>
                                            <li>Navigate to the folder contained within the zip, likely <code>nes-puzzle-maker-{CONSTANTS.GithubTag}</code></li>
                                            <li>Run <code>create-nes-game download-dependencies</code> to do first-time setup</li>
                                            <li>Run <code>create-nes-game build</code> to build your game</li>
                                            <li>Your rom will be created in the <code>rom/</code> folder! (Or play it with <code>create-nes-game run</code>)</li>
                                        </ol>
                                        <p>
                                            <strong>Note:</strong> When you start editing the engine, you may want to switch to the latest downloadble version of the tool. The web-based tool will
                                            continue to be updated, and may add features that your rom version doesn't support. While exporting a resource zip from a newer version of Retro Puzzle Maker
                                            into your project might work, this is not officially supported.
                                        </p>
                                        <h3>Editing Code</h3>
                                        <p>There are a number of modern tools that can be used to edit your game. The tools below are recommended and work well, but other options can also be used!</p>
                                        <ul>
                                            <li><strong>Suggested Editor:</strong> <a href="https://code.visualstudio.com/" target="_blank" rel="noreferrer">Visual Studio Code</a></li>
                                            <li><strong>Suggested C Plugin:</strong> <a href="https://marketplace.visualstudio.com/items?itemName=ms-vscode.cpptools" target="_blank" rel="noreferrer">Microsoft's C/C++ Extension</a></li>
                                            <li><strong>Optional ASM Plugin:</strong> <a href="https://marketplace.visualstudio.com/items?itemName=mirao.mads" target="_blank" rel="noreferrer">MADS</a> (You probably won't need this!)</li>
                                        </ul>
                                        <p>Once you set those up, you can open any C file in <code>source/c</code> folder to start editing. You may want to start by looking at either <code>source/c/main.c</code> or <code>source/c/sprites/player.c</code></p>
                                        <h3>Updating Levels/etc</h3>
                                        <p>Even if you're reworking the engine, you can still use the web editor for level designs, tiles, and whatever else you like! The only new thing is you'll have to download the updated files and add them to your project. Here's how:</p>
                                        <ol>
                                            <li><Button variant="primary" onClick={() => this.generateCodeZip(false)}>Download Your Game's Resources</Button></li>
                                            <li>Extract the zip file to a new directory</li>
                                            <li>Navigate to the folder contained within the zip, likely <code>nes-puzzle-maker-{CONSTANTS.GithubTag}</code></li>
                                            <li>Copy everything over your existing folder. Overwrite anything it asks you to</li>
                                            <li>Build your game and see your changes!</li>
                                        </ol>

                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={4}>
                            <Card className="quick-links">
                                <Card.Header>Quick Links</Card.Header>
                                <Card.Body>
                                    <Card.Text as="div" className="inner">
                                        <ul>
                                            <li><Button variant="outline-primary" onClick={() => this.generateCodeZip(true)}>Download Your Game's Source</Button></li>
                                            <li><Button variant="outline-primary" onClick={() => this.generateCodeZip(false)}>Download Your Game's Resources</Button></li>
                                            <li><Button variant="outline-secondary" onClick={e => this.downloadPatchableData(e)}>Download patchable_data.asm</Button></li>
                                            <li><Button variant="outline-secondary" onClick={e => this.getIpsData(e)}>Download IPS Patch</Button></li>
                                            <li><Button variant="outline-secondary" onClick={() => this.doDialog('romOffset')}>Show Rom Offsets</Button></li>
                                        </ul>
                                        <a rel="noreferrer" href={CONSTANTS.GithubRepoUrl} target="_blank">Base game source (Github)</a>
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="mt-5">
                                <h2>Code FAQs</h2>
                                <Accordion flush>
                                    <Accordion.Item eventKey="code-1">
                                        <Accordion.Header>Why create-nes-game?</Accordion.Header>
                                        <Accordion.Body>
                                            <p><a target="_blank" rel="noreferrer" href="https://create-nes-game.nes.science/">create-nes-game</a> tries to create a one-stop-shop for all the tools required for NES development. It takes care of obtaining the right compiler/version, path setup, and some file type conversions for you. It is one of the easiest ways to get started.</p>
                                            <p>If you would prefer not use the tool, behind the scenes it uses cc65 and neslib - you are not required to use create-nes-game. </p>
                                            <p>Note: This tool and create-nes-game share an author.</p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey="code-2">
                                        <Accordion.Header>How can I learn more about C on the NES?</Accordion.Header>
                                        <Accordion.Body>
                                            <p>
                                                One option to learn more is to explore <a href="https://nes-starter-kit.nes.science" target="_blank" rel="noreferrer">nes-starter-kit</a>, a getting starting guide in C. That guide uses some of the same tools, though it targets creating a top-down action/adventure game. It walks through some of the basics using code for an example game. There are many similarities between the codebases.
                                            </p>
                                            <p>
                                                Another option is to simply follow a guide to learn C. C on the NES is just C. The tooling available limits some more modern features, but all of the basics should apply.
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey="code-3">
                                        <Accordion.Header>Can I update my engine's code if I've been manually editing it?</Accordion.Header>
                                        <Accordion.Body>
                                            <p>
                                                Unfortunately, this isn't an easy thing to. You can get the engine source code from <a href="https//gh.nes.science/nes-puzzle-maker-rom" target="_blank" rel="noreferrer">Github</a>, or download a newer zip file from this page. 
                                                You will then need to diff the two folders and update accordingly.
                                            </p>
                                            <p>
                                                If you are using git and have everything committed with no local changes, you could extract a newer version of the zip over your code, then use <code>git diff</code>{" "}
                                                to determine what changed and bring your changes back. Be very, very sure you have no local changes first, though!
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="code-4">
                                        <Accordion.Header>I need help!</Accordion.Header>
                                        <Accordion.Body>
                                            <p>
                                                The easiest option for getting help is to reach out with an issue against <a href="https://gh.nes.science/nes-puzzle-maker-rom/issues" target="_blank" rel="noreferrer">the game's Github</a>. This repository is actively maintained alongside the app.
                                            </p>
                                            <p>
                                                Alternatively, feel free to email me at <a href="mailto:admin@nes.science" target="_blank" rel="noreferrer">admin@nes.science</a>. 
                                            </p>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>

                            </div>
                        </Col>
                    </Row>
                </Container>
                <RomAddressOffsetDialog visible={this.state.dialogVisibility.romOffset} onClose={e => this.doDialog(e, 'romOffset', false)} />

            </div>
        );
    }
}